/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { propTypesEventTag } from '../../templates/EventTag/EventTag.proptypes';
import {
  propTypesAuthor,
  propTypesPost,
} from '../../templates/Post/Post.proptypes';
import { propTypesTag } from '../../templates/Tag/Tag.proptypes';

export const propTypesGatedContent = {
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  institution: PropTypes.string,
  job: PropTypes.string,
};

export const propTypesGatsbyImage = {
  images: PropTypes.shape({
    sources: PropTypes.array,
    fallback: PropTypes.shape({
      src: PropTypes.string,
      srcset: PropTypes.string,
      sizes: PropTypes.string,
    }),
  }),
  layout: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export const propTypesIllustration = {
  alt: PropTypes.string,
  localFile: PropTypes.shape({
    gatsbyImageData: PropTypes.shape(propTypesGatsbyImage),
    staticURL: PropTypes.string,
  }),
};

export const propTypesLink = {
  id: PropTypes.string,
  lang: PropTypes.string,
  target: PropTypes.string,
  type: PropTypes.string,
  uid: PropTypes.string,
  url: PropTypes.string,
};

export const propTypesTextHtml = {
  html: PropTypes.string,
  text: PropTypes.string,
};

export const propTypesAltLanguage = PropTypes.shape({
  lang: PropTypes.string,
  type: PropTypes.string,
  uid: PropTypes.string,
  url: PropTypes.string,
});

export const propTypesActiveDocMeta = PropTypes.shape({
  alternateLanguages: PropTypes.arrayOf(PropTypes.shape(propTypesAltLanguage)),
  lang: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  uid: PropTypes.string,
});

export const propTypesSite = {
  buildTime: PropTypes.string,
};

export const propTypeReview = PropTypes.shape({
  review_body: PropTypes.string,
  review_name: PropTypes.string,
  reviewer_firstname: PropTypes.string,
  reviewer_job_title: PropTypes.string,
  reviewer_lastname: PropTypes.string,
});

export const propTypesFAQContent = PropTypes.shape({
  answer: PropTypes.string,
  question: PropTypes.string,
});

export const propTypesPopulars = {
  alternate_languages: PropTypes.arrayOf(PropTypes.shape(propTypesAltLanguage)),
  data: PropTypes.shape({
    max_posts_popular: PropTypes.string,
    max_posts_popular_in: PropTypes.string,
    popular_in_posts: PropTypes.arrayOf(
      PropTypes.shape({
        popular_in_post: PropTypes.shape(propTypesPost),
      })
    ),
    popular_posts: PropTypes.arrayOf(
      PropTypes.shape({
        popular_post: PropTypes.shape(propTypesPost),
      })
    ),
  }),
  lang: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
};

export const propTypesEventCategory = {
  alternate_languages: PropTypes.arrayOf(PropTypes.shape(propTypesAltLanguage)),
  data: PropTypes.shape({
    category: PropTypes.shape(propTypesEventTag),
    order: PropTypes.number,
    sub_tags: PropTypes.arrayOf(
      PropTypes.shape({
        sub_tag: PropTypes.shape(propTypesEventTag),
      })
    ),
  }),
  first_publication_date: PropTypes.string,
  lang: PropTypes.string,
  last_publication_date: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
};

export const propTypesUTMs = {
  utm_source: PropTypes.string,
  utm_medium: PropTypes.string,
  utm_campaign: PropTypes.string,
  utm_content: PropTypes.string,
  utm_term: PropTypes.string,
};

export const propTypesLocalization = {
  type: PropTypes.string,
  lang: PropTypes.string,
  data: PropTypes.shape({
    all_articles_section_title: PropTypes.string,
    author_section_label: PropTypes.string,
    banner_background: PropTypes.shape(propTypesIllustration),
    banner_cta_datatrack_attribute: PropTypes.string,
    banner_cta_label: PropTypes.string,
    banner_cta_link: PropTypes.shape(propTypesLink),
    banner_exceptions: PropTypes.arrayOf(
      PropTypes.shape({
        exception: PropTypes.shape({
          uid: PropTypes.string,
        }),
      })
    ),
    banner_icon: PropTypes.string,
    banner_illustration: PropTypes.shape(propTypesIllustration),
    banner_subtitle: PropTypes.shape(propTypesTextHtml),
    banner_theme: PropTypes.string,
    banner_title: PropTypes.string,
    contact_cta_label: PropTypes.string,
    cookie_button_accept: PropTypes.string,
    cookie_button_back: PropTypes.string,
    cookie_button_confirm: PropTypes.string,
    cookie_button_custom: PropTypes.string,
    cookie_button_reject: PropTypes.string,
    cookie_intro: PropTypes.shape(propTypesTextHtml),
    default_template_author: PropTypes.shape(propTypesAuthor),
    email_label_field: PropTypes.string,
    email_placeholder_field: PropTypes.string,
    essential_description: PropTypes.string,
    essential_label: PropTypes.string,
    establishment_placeholder_field: PropTypes.string,
    establishment_label_field: PropTypes.string,
    event_author_prefix: PropTypes.string,
    event_creator_section: PropTypes.string,
    event_cta_title: PropTypes.string,
    event_cta_label: PropTypes.string,
    event_searchbar_placeholder: PropTypes.string,
    event_themes_title: PropTypes.string,
    events_filtered_by: PropTypes.string,
    events_more_recent: PropTypes.string,
    events_less_recent: PropTypes.string,
    events_more_liked: PropTypes.string,
    filter_dropdown_label: PropTypes.string,
    first_name_field: PropTypes.string,
    first_name_placeholder_field: PropTypes.string,
    functional_description: PropTypes.string,
    functional_label: PropTypes.string,
    highlight_article_button_label: PropTypes.string,
    jobs_field: PropTypes.arrayOf(
      PropTypes.shape({
        job_category: PropTypes.string,
        job_name: PropTypes.string,
        job_pipedrive_id: PropTypes.string,
        job_hubspot_id: PropTypes.string,
      })
    ),
    job_label_field: PropTypes.string,
    last_name_field: PropTypes.string,
    last_name_placeholder_field: PropTypes.string,
    licenses_label_field: PropTypes.string,
    licenses_number: PropTypes.arrayOf(
      PropTypes.shape({
        license_max: PropTypes.number,
        license_min: PropTypes.number,
        license_pipedrive_id: PropTypes.string,
        license_hubspot_id: PropTypes.string,
      })
    ),
    modal_confirm: PropTypes.string,
    modal_field_error: PropTypes.string,
    modal_internal_error: PropTypes.string,
    modal_ok_cta: PropTypes.string,
    modal_email_invalid: PropTypes.string,
    more_context_label: PropTypes.string,
    heard_about_label_field: PropTypes.string,
    heard_about_options: PropTypes.arrayOf(
      PropTypes.shape({
        option_label: PropTypes.string,
        option_hubspot_id: PropTypes.string,
      })
    ),
    most_popular_section_title: PropTypes.string,
    // Newsletter
    newsletter_already_sub: PropTypes.string,
    newsletter_conditions: PropTypes.string,
    newsletter_description: PropTypes.string,
    newsletter_description_side: PropTypes.string,
    newsletter_error_message: PropTypes.string,
    newsletter_invalid_email: PropTypes.string,
    newsletter_placeholder: PropTypes.string,
    newsletter_subscription_button: PropTypes.string,
    newsletter_title: PropTypes.string,
    newsletter_title_side: PropTypes.string,
    newsletter_validation_button: PropTypes.string,
    newsletter_validation_message: PropTypes.string,

    organization_label_field: PropTypes.string,
    organization_placeholder_field: PropTypes.string,
    organization_types: PropTypes.arrayOf(
      PropTypes.shape({
        organization_category: PropTypes.string,
        organization_type: PropTypes.string,
        organization_pipedrive_id: PropTypes.string,
        organization_hubspot_id: PropTypes.string,
      })
    ),
    organization_type_label_field: PropTypes.string,
    pay_cta_label: PropTypes.string,
    performance_description: PropTypes.string,
    performance_label: PropTypes.string,
    phone_label_field: PropTypes.string,
    phone_placeholder_field: PropTypes.string,
    popular_in_section_title: PropTypes.string,
    question_number: PropTypes.string,
    questions_number: PropTypes.string,
    recommended_section_label: PropTypes.string,
    recurring_title: PropTypes.string,
    searchbar_placeholder: PropTypes.string,
    see_all_button_label: PropTypes.string,
    tab_field_label: PropTypes.string,
    select_placeholder: PropTypes.string,
    session_counter: PropTypes.string,
    session_label: PropTypes.string,
    share_event_description: PropTypes.string,
    share_event_label: PropTypes.string,
    share_event_link: PropTypes.shape(propTypesLink),
    share_event_title: PropTypes.shape(propTypesTextHtml),
    share_label: PropTypes.string,
    tag_list_error: PropTypes.string,
    tag_list_label: PropTypes.string,
    tag_section_label: PropTypes.string,
    testimonial_tag: PropTypes.shape(propTypesTag),
    time_unit: PropTypes.string,
    time_unit_plural: PropTypes.string,
    tracking_description: PropTypes.string,
    tracking_label: PropTypes.string,
    use_event_template_label: PropTypes.string,
    user_selector: PropTypes.string,
    users_selector: PropTypes.string,
    see_event_template_label: PropTypes.string,
    woobinar_access_code_cta: PropTypes.string,
    woobinar_already_subscribed: PropTypes.string,
    woobinar_register_cta: PropTypes.string,
    woobinar_replay_cta: PropTypes.string,
    woobinars_tag: PropTypes.shape(propTypesTag),
    // Loginless Spin The Wheel
    stw_title: PropTypes.string,
    stw_blur_label: PropTypes.string,
    stw_remove_option: PropTypes.string,
    stw_reinit_wheel: PropTypes.string,
    stw_remaining: PropTypes.string,
    stw_catchy_cta: PropTypes.string,
    stw_signup_cta: PropTypes.string,
    stw_signup_link: PropTypes.shape(propTypesLink),
    stw_modal_cancel: PropTypes.string,
    stw_result_title: PropTypes.string,
    stw_min: PropTypes.string,
    stw_max: PropTypes.string,
    // Loginless wordcloud
    wc_title: PropTypes.string,
    wc_copy_paste: PropTypes.string,
    wc_upload: PropTypes.string,
    wc_upload_dropping_message: PropTypes.string,
    wc_placeholder: PropTypes.string,
    wc_textarea_info: PropTypes.string,
    wc_upload_instruction: PropTypes.string,
    wc_instruction_alt: PropTypes.string,
    wc_formats: PropTypes.string,
    wc_max_size: PropTypes.string,
    wc_csv: PropTypes.string,
    wc_csv_words: PropTypes.string,
    wc_frequency: PropTypes.string,
    wc_example_template: PropTypes.string,
    wc_example_document: PropTypes.shape(propTypesLink),
    wc_settings: PropTypes.string,
    wc_word_limit_label: PropTypes.string,
    wc_word_limit_info: PropTypes.string,
    wc_stop_words_label: PropTypes.string,
    wc_stop_words_info: PropTypes.string,
    wc_start_over_label: PropTypes.string,
    wc_generate: PropTypes.string,
    wc_wooclap_brand_mark: PropTypes.string,
    wc_wordcloud_info: PropTypes.string,
    wc_catchy_cta_label: PropTypes.string,
    wc_download_cta_label: PropTypes.string,
    wc_too_large_file_error: PropTypes.string,
    wc_too_large_file_info: PropTypes.string,
    wc_invalid_format_error: PropTypes.string,
    wc_invalid_format_info: PropTypes.string,
    wc_wrong_csv_structure_error: PropTypes.string,
    wc_wrong_csv_structure_info: PropTypes.string,
    wc_timeout_error: PropTypes.string,
    wc_timeout_info: PropTypes.string,
    wc_internal_error: PropTypes.string,
    wc_internal_info: PropTypes.string,
    wc_start_over_modal_title: PropTypes.string,
    wc_start_over_modal_content: PropTypes.string,
    wc_start_over_modal_cancel: PropTypes.string,
    wc_start_over_modal_continue: PropTypes.string,
    wc_catchy_modal_title: PropTypes.string,
    wc_catchy_modal_content: PropTypes.string,
    wc_download_modal_title: PropTypes.string,
    wc_download_modal_content: PropTypes.string,
    wc_modal_cancel: PropTypes.string,
    wc_catchy_modal_label: PropTypes.string,
    wc_download_modal_label: PropTypes.string,
    wc_export_png: PropTypes.string,
    wc_export_jpg: PropTypes.string,
    wc_export_svg: PropTypes.string,
    wc_export_pdf: PropTypes.string,
    wc_export_csv: PropTypes.string,
    wc_tooltip: PropTypes.string,
    // Definition
    recommanded_section_title: PropTypes.string,
    // 404
    no_posts: PropTypes.string,
    // Pricing
    pricing_ai_label: PropTypes.string,
    pricing_contact_label: PropTypes.string,
    pricing_contact_info: PropTypes.string,
  }),
};
